import { all, select, takeEvery, takeLatest, fork, call, put } from 'redux-saga/effects'

// action
import { shipmentActions, orderActions, routeActions } from '@redux/store/index'

// api
import { shipmentApi, seasonApi, ResType } from '@api'

// lib
import { Logger, printLabel } from '@utils'
import { writeBoFilter } from '@utils/BackofficeFilter'

// type
import {
	Shipment,
	Dispatch,
	DispatchItem,
	DispatchType,
	DispatchDetailType,
	ShipmentLabelFileType,
	ShipmentType,
	ShipmentLabelType,
	ShipmentTrackResultType,
	Season,
	SeasonType,
} from '@schemas'
import { ShipmentStateType } from '@redux/types'

const LIST_PATH = '/main/shipping'
const detailPath = (dispatchId: string): string =>
	`${LIST_PATH}/${encodeURIComponent(dispatchId)}`

/** 2xx 면 성공. requestManager 는 POST 에 201 을 준다 — 200 만 보면 추적·라벨 생성이 실패로 보인다 */
export const isOk = (res: ResType | undefined): boolean =>
	res !== undefined && res.status >= 200 && res.status < 300

/**
 * API 오류 본문에서 문장 하나. 서버는 `{ message: { errorMessage, meta: { title } } }` 로 준다.
 * title 이 배열(스택)이면 첫 줄만.
 */
export const errorText = (res: ResType | undefined, fallback: string): string => {
	const data: any = res?.data
	const msg = data?.message ?? data
	const title = msg?.meta?.title
	if (Array.isArray(title)) return String(title[0] ?? fallback)
	if (typeof title === 'string' && title !== '') return title
	if (typeof msg?.errorMessage === 'string') return msg.errorMessage
	if (typeof msg === 'string') return msg
	return fallback
}

/** 시즌 필터 옵션 — 연도 무관 전체 시즌. 오더 관리와 같은 목록을 따로 읽는다 */
function* fetchSeasonOptions() {
	try {
		const res: ResType = yield call(seasonApi.getSeason, { query: { year: '' } })
		if (isOk(res)) {
			const list: SeasonType[] = (res.data as any[]).map(a => new Season(a))
			yield put(shipmentActions.SEASON_OPTIONS(list))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 배송 관리 = 출고 대장 목록 — **행 = 배송 건(박스)** (`GET /shipment/board`, DISPATCH_PLAN §3).
 * 기준은 월(주문일, 기본) · 기간(주문일) · 시즌. 조회할 때마다 기준 · 시즌 · 상태 · 종류 · 채널을 적어 둔다.
 */
function* fetchShipmentList() {
	try {
		const searchStatus: string = yield select(state => state.shipment.searchStatus)
		const searchKeyword: string = yield select(state => state.shipment.searchKeyword)
		const searchMode: ShipmentStateType['searchMode'] = yield select(
			state => state.shipment.searchMode,
		)
		const searchMonth: string = yield select(state => state.shipment.searchMonth)
		const searchSeasonId: string = yield select(state => state.shipment.searchSeasonId)
		const searchStartDate: string = yield select(state => state.shipment.searchStartDate)
		const searchEndDate: string = yield select(state => state.shipment.searchEndDate)
		const searchKind: string = yield select(state => state.shipment.searchKind)
		const searchChannel: string = yield select(state => state.shipment.searchChannel)

		writeBoFilter('shipment', {
			mode: searchMode,
			seasonId: searchSeasonId,
			status: searchStatus,
			kind: searchKind,
			channel: searchChannel,
		})

		yield put(shipmentActions.SHIPMENT_LIST_STATUS('loading'))

		/*
			기준이 아닌 조건은 보내지 않는다 — 숨은 날짜 피커의 기본값(이번 달)이 서버에 남으면
			'전체 시즌' 이 조용히 '이번 달' 로 조회된다(오더 관리와 같은 함정).
		*/
		const apiRes: ResType = yield call(shipmentApi.getDispatchBoard, {
			query: {
				status: searchStatus,
				keyword: searchKeyword,
				month: searchMode === 'month' ? searchMonth : '',
				startDate: searchMode === 'date' ? searchStartDate : '',
				endDate: searchMode === 'date' ? searchEndDate : '',
				seasonId: searchMode === 'season' ? searchSeasonId : '',
				kind: searchKind,
				channel: searchChannel,
			},
		})
		if (isOk(apiRes)) {
			const list: DispatchType[] = (apiRes.data as any[]).map(a => new Dispatch(a))
			yield put(shipmentActions.SHIPMENT_LIST(list))
			yield put(shipmentActions.SHIPMENT_LIST_STATUS(list.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(shipmentActions.SHIPMENT_LIST_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/**
 * 자세히 한 장 — `GET /shipment/board/:dispatchId`.
 * 배송 건 · 품목 줄 · 박스 · 라벨 이력 · 라벨 폼 초기값을 한 번에 받는다(추가 조회 없음).
 */
function* fetchDetail(action: { type: string; payload: string }) {
	const dispatchId: string = action.payload
	if (dispatchId === '') {
		yield put(shipmentActions.DETAIL_STATUS('init'))
		return
	}
	try {
		const apiRes: ResType = yield call(shipmentApi.getDispatchOne, {
			params: { dispatchId },
		})
		if (isOk(apiRes) && Array.isArray(apiRes.data) && apiRes.data[0] !== undefined) {
			const raw = apiRes.data[0] as any
			const detail: DispatchDetailType = {
				dispatch: new Dispatch(raw.dispatch),
				items: (raw.items ?? []).map((i: any) => new DispatchItem(i)),
				boxes: Array.isArray(raw.boxes) ? raw.boxes : [],
				shipments: (raw.shipments ?? []).map((s: any) => new Shipment(s)),
				prefill: raw.prefill,
			}
			yield put(shipmentActions.DETAIL(detail))
			yield put(shipmentActions.DETAIL_STATUS('ok'))
		} else {
			yield put(shipmentActions.DETAIL(null))
			yield put(shipmentActions.DETAIL_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(shipmentActions.DETAIL(null))
		yield put(shipmentActions.DETAIL_STATUS('empty'))
	}
}

/**
 * 저장된 라벨 한 건 — `GET /shipment/:shipmentId/label`.
 * 서버가 S3 에서 읽어 base64 로 준다. 화면은 이것으로 미리보기·인쇄·내려받기를 한다.
 */
function* fetchLabelFile(action: { type: string; payload: string }) {
	const shipmentId: string = action.payload
	if (shipmentId === '') {
		yield put(shipmentActions.LABEL_FILE_STATUS('init'))
		return
	}
	try {
		const apiRes: ResType = yield call(shipmentApi.getShipmentLabel, {
			params: { shipmentId },
		})
		if (isOk(apiRes) && Array.isArray(apiRes.data) && apiRes.data[0] !== undefined) {
			yield put(shipmentActions.LABEL_FILE(apiRes.data[0] as ShipmentLabelFileType))
			yield put(shipmentActions.LABEL_FILE_STATUS('ok'))
		} else {
			yield put(shipmentActions.LABEL_FILE(null))
			yield put(shipmentActions.LABEL_FILE_STATUS('empty'))
			yield put(shipmentActions.MESSAGE(errorText(apiRes, '라벨을 읽지 못했습니다.')))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(shipmentActions.LABEL_FILE(null))
		yield put(shipmentActions.LABEL_FILE_STATUS('empty'))
	}
}

/** 주문 자세히 모달 — `GET /shipment?orderId=` 로 그 주문의 라벨만 읽는다. '' 이면 리듀서가 비운다 */
function* fetchOrderShipments(action: { type: string; payload: string }) {
	if (action.payload === '') return
	try {
		yield put(shipmentActions.ORDER_SHIPMENTS_STATUS('loading'))
		const apiRes: ResType = yield call(shipmentApi.getShipment, {
			query: { status: 'all', orderId: action.payload },
		})
		if (isOk(apiRes)) {
			const list: ShipmentType[] = (apiRes.data as any[]).map(a => new Shipment(a))
			yield put(shipmentActions.ORDER_SHIPMENTS(list))
			yield put(shipmentActions.ORDER_SHIPMENTS_STATUS(list.length > 0 ? 'ok' : 'empty'))
		} else {
			yield put(shipmentActions.ORDER_SHIPMENTS([]))
			yield put(shipmentActions.ORDER_SHIPMENTS_STATUS('empty'))
		}
	} catch (error) {
		Logger.warn(error)
		yield put(shipmentActions.ORDER_SHIPMENTS_STATUS('empty'))
	}
}

function* triggerInitShipping() {
	yield call(fetchSeasonOptions)
	yield call(fetchShipmentList)
}

function* track() {
	try {
		const target: ShipmentStateType['triggerTrack'] = yield select(
			state => state.shipment.triggerTrack,
		)
		yield put(shipmentActions.TRACKING(true))
		const apiRes: ResType = yield call(shipmentApi.trackShipment, {
			body: {
				shipmentIds: target.shipmentIds,
				orderIds: target.orderIds,
				dispatchIds: target.dispatchIds,
			},
		})
		if (isOk(apiRes)) {
			const r = apiRes.data[0] as ShipmentTrackResultType | undefined
			yield put(
				shipmentActions.MESSAGE(
					r === undefined || r.checked === 0
						? '추적할 배송 건이 없습니다.'
						: `${r.checked}건 조회, ${r.delivered}건 배송 완료로 바뀌었습니다.`,
				),
			)
		} else {
			yield put(shipmentActions.MESSAGE(errorText(apiRes, '추적 갱신 실패')))
		}
		yield put(shipmentActions.TRACKING(false))

		if (target.refreshOrders) {
			yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		} else {
			yield call(fetchShipmentList)
			const detailId: string = yield select(state => state.shipment.detailDispatchId)
			if (detailId !== '') yield put(shipmentActions.FETCH_DETAIL(detailId))
		}
	} catch (error) {
		yield put(shipmentActions.TRACKING(false))
		Logger.warn(error)
	}
}

function* createFedex() {
	try {
		const body: ShipmentStateType['triggerCreateFedex'] = yield select(
			state => state.shipment.triggerCreateFedex,
		)
		if (body === null) return
		yield put(shipmentActions.CREATING(true))

		const apiRes: ResType = yield call(shipmentApi.createFedexShipment, { body })
		if (isOk(apiRes)) {
			const result = apiRes.data[0] as {
				shipment: ShipmentType
				label: { mime: string; base64: string }
			}
			// 발급 직후 인쇄 창을 연다 — 현장에서 바로 붙인다
			printLabel({
				base64: result.label.base64,
				mime: result.label.mime,
				fileName: `label-${result.shipment.trackingNumber}`,
			})
			yield put(shipmentActions.CREATING(false))
			yield put(shipmentActions.LABEL_FORM_OPEN(false))
			yield put(
				shipmentActions.MESSAGE(
					`라벨을 만들었습니다. 송장 ${result.shipment.trackingNumber}`,
				),
			)
			// 폼을 닫고 라벨 보기로 — 다시 인쇄할 자리가 그곳이다
			yield put(routeActions.NAVIGATE_PATH(`${detailPath(body.dispatchId)}/label`))
			yield put(shipmentActions.FETCH_DETAIL(body.dispatchId))
			yield put(shipmentActions.TRIGGER_SHIPMENT_SEARCH(true))
			yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		} else {
			yield put(shipmentActions.CREATING(false))
			yield put(shipmentActions.MESSAGE(errorText(apiRes, 'FedEx 라벨 생성 실패')))
		}
	} catch (error) {
		yield put(shipmentActions.CREATING(false))
		Logger.warn(error)
	}
}

function* printStoredLabel() {
	try {
		const target: ShipmentStateType['triggerPrintLabel'] = yield select(
			state => state.shipment.triggerPrintLabel,
		)
		let shipmentId = target.shipmentId

		if (shipmentId === '' && target.orderId !== '') {
			const listRes: ResType = yield call(shipmentApi.getShipment, {
				query: { status: 'all', orderId: target.orderId },
			})
			const active = ((listRes.data as any[]) ?? [])
				.map(a => new Shipment(a))
				.find(s => s.canceledAt === '' && s.hasLabel === 1)
			shipmentId = active?.id ?? ''
		}
		if (shipmentId === '') {
			yield put(shipmentActions.MESSAGE('저장된 라벨이 없습니다. 라벨을 먼저 만드세요.'))
			return
		}

		const apiRes: ResType = yield call(shipmentApi.getShipmentLabel, {
			params: { shipmentId },
		})
		if (apiRes.status === 200) {
			const label = apiRes.data[0] as ShipmentLabelType
			printLabel({
				base64: label.base64,
				mime: label.mime,
				fileName: `label-${label.trackingNumber}`,
			})
		} else {
			yield put(shipmentActions.MESSAGE(errorText(apiRes, '라벨을 읽지 못했습니다.')))
		}
	} catch (error) {
		Logger.warn(error)
	}
}

/** 수동 배송 건 추가 — 만들고 그 건의 자세히로 옮긴다 */
function* createDispatch() {
	try {
		const body: ShipmentStateType['triggerCreateDispatch'] = yield select(
			state => state.shipment.triggerCreateDispatch,
		)
		if (body === null) return
		const apiRes: ResType = yield call(shipmentApi.createDispatch, { body })
		yield put(shipmentActions.SAVING(false))
		if (isOk(apiRes) && Array.isArray(apiRes.data) && apiRes.data[0]?.id) {
			const id = String(apiRes.data[0].id)
			yield put(shipmentActions.MESSAGE('배송 건을 추가했습니다.'))
			yield put(routeActions.NAVIGATE_PATH(detailPath(id)))
			yield call(fetchShipmentList)
		} else {
			yield put(shipmentActions.MESSAGE(errorText(apiRes, '배송 건 추가 실패')))
		}
	} catch (error) {
		yield put(shipmentActions.SAVING(false))
		Logger.warn(error)
	}
}

/** 배송 건 저장 — 끝나면 자세히와 목록을 다시 읽는다(주문 미러까지 반영되게 오더 목록도) */
function* updateDispatch() {
	try {
		const target: ShipmentStateType['triggerUpdateDispatch'] = yield select(
			state => state.shipment.triggerUpdateDispatch,
		)
		if (target === null) return
		const apiRes: ResType = yield call(shipmentApi.updateDispatch, {
			params: { dispatchId: target.dispatchId },
			body: target.body,
		})
		yield put(shipmentActions.SAVING(false))
		if (isOk(apiRes)) {
			yield put(shipmentActions.MESSAGE('저장했습니다.'))
			yield put(shipmentActions.FETCH_DETAIL(target.dispatchId))
			yield call(fetchShipmentList)
			yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		} else {
			yield put(shipmentActions.MESSAGE(errorText(apiRes, '저장 실패')))
		}
	} catch (error) {
		yield put(shipmentActions.SAVING(false))
		Logger.warn(error)
	}
}

/** 소프트 삭제 — 성공하면 목록으로 */
function* deleteDispatch() {
	try {
		const dispatchId: string = yield select(state => state.shipment.triggerDeleteDispatch)
		if (dispatchId === '') return
		const apiRes: ResType = yield call(shipmentApi.deleteDispatch, {
			params: { dispatchId },
		})
		yield put(shipmentActions.SAVING(false))
		if (isOk(apiRes)) {
			yield put(shipmentActions.MESSAGE('배송 건을 지웠습니다.'))
			yield put(routeActions.NAVIGATE_PATH(LIST_PATH))
			yield call(fetchShipmentList)
			yield put(orderActions.TRIGGER_ORDER_SEARCH(true))
		} else {
			yield put(shipmentActions.MESSAGE(errorText(apiRes, '삭제 실패')))
		}
	} catch (error) {
		yield put(shipmentActions.SAVING(false))
		Logger.warn(error)
	}
}

/** 박스 추가 — 새 박스의 자세히로 옮긴다 */
function* addBox() {
	try {
		const dispatchId: string = yield select(state => state.shipment.triggerAddBox)
		if (dispatchId === '') return
		const apiRes: ResType = yield call(shipmentApi.addDispatchBox, {
			params: { dispatchId },
		})
		yield put(shipmentActions.SAVING(false))
		if (isOk(apiRes) && Array.isArray(apiRes.data) && apiRes.data[0]?.id) {
			const id = String(apiRes.data[0].id)
			yield put(shipmentActions.MESSAGE('박스를 추가했습니다. 품목 줄을 옮겨 담습니다.'))
			yield put(routeActions.NAVIGATE_PATH(detailPath(id)))
			yield call(fetchShipmentList)
		} else {
			yield put(shipmentActions.MESSAGE(errorText(apiRes, '박스 추가 실패')))
		}
	} catch (error) {
		yield put(shipmentActions.SAVING(false))
		Logger.warn(error)
	}
}

function* mainSaga() {
	yield takeEvery(shipmentActions.TRIGGER_INIT_SHIPPING_PAGE, triggerInitShipping)
	yield takeLatest(shipmentActions.TRIGGER_SHIPMENT_SEARCH, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_STATUS, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_MODE, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_MONTH, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_SEASON_ID, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_START_DATE, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_END_DATE, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_KIND, fetchShipmentList)
	yield takeLatest(shipmentActions.SEARCH_CHANNEL, fetchShipmentList)
	yield takeLatest(shipmentActions.TRIGGER_ORDER_SHIPMENTS, fetchOrderShipments)
	yield takeLatest(shipmentActions.FETCH_DETAIL, fetchDetail)
	yield takeLatest(shipmentActions.FETCH_LABEL_FILE, fetchLabelFile)
	yield takeEvery(shipmentActions.TRIGGER_TRACK, track)
	yield takeEvery(shipmentActions.TRIGGER_CREATE_FEDEX, createFedex)
	yield takeEvery(shipmentActions.TRIGGER_PRINT_LABEL, printStoredLabel)
	yield takeEvery(shipmentActions.TRIGGER_CREATE_DISPATCH, createDispatch)
	yield takeEvery(shipmentActions.TRIGGER_UPDATE_DISPATCH, updateDispatch)
	yield takeEvery(shipmentActions.TRIGGER_DELETE_DISPATCH, deleteDispatch)
	yield takeEvery(shipmentActions.TRIGGER_ADD_BOX, addBox)
}

export default function* rootSaga() {
	yield all([fork(mainSaga)])
}
